import { PaperGradientBG } from "../kits";
import rough from "roughjs/bundled/rough.esm.js";
import { useEffect, useLayoutEffect, useState, useRef } from "react";
import TerminalDetailOverlay from "./TerminalDetailOverlay";
import { COMPANY_DETAIL_CONFIG, toCompanyKey } from "./CompanyDetailConfig";

function RoughCircle({ size = 56, color = "#AC8E66", strokeWidth = 1.5, roughness = 1.5, className = "" }) {
  const svgRef = useRef(null);
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  useLayoutEffect(() => {
    if (!isClient || !svgRef.current) return;
    svgRef.current.innerHTML = "";
    const rc = rough.svg(svgRef.current);
    const padding = strokeWidth;
    const node = rc.circle(size / 2, size / 2, size - padding * 2, {
      roughness,
      stroke: color,
      strokeWidth,
      fill: "none",
    });
    svgRef.current.appendChild(node);
  }, [isClient, size, color, strokeWidth, roughness]);

  if (!isClient) return null;

  return (
    <svg
      ref={svgRef}
      width={size}
      height={size}
      className={`absolute inset-0 pointer-events-none ${className}`}
    />
  );
}

export default function CompanyTerminal({
  companies = [],
  onCompanyClick,
  enableDetailOverlay = false,
  withBackground = true,
}) {
  const [activeCompanyKey, setActiveCompanyKey] = useState(null);
  const orderedCompanyKeys = companies
    .map((company) => toCompanyKey(company.name))
    .filter((key, index, arr) => arr.indexOf(key) === index && Boolean(COMPANY_DETAIL_CONFIG[key]));
  const activeCompanyIndex = activeCompanyKey ? orderedCompanyKeys.indexOf(activeCompanyKey) : -1;
  const activeCompanyDetail = activeCompanyKey ? COMPANY_DETAIL_CONFIG[activeCompanyKey] : null;

  const handleCompanyClick = (companyName) => {
    if (enableDetailOverlay) {
      const key = toCompanyKey(companyName);
      if (COMPANY_DETAIL_CONFIG[key]) {
        setActiveCompanyKey(key);
        return;
      }
    }
    onCompanyClick?.();
  };
  const handlePrev = () => {
    if (activeCompanyIndex <= 0) return;
    setActiveCompanyKey(orderedCompanyKeys[activeCompanyIndex - 1]);
  };
  const handleNext = () => {
    if (activeCompanyIndex < 0 || activeCompanyIndex >= orderedCompanyKeys.length - 1) return;
    setActiveCompanyKey(orderedCompanyKeys[activeCompanyIndex + 1]);
  };

  const overlay = (
    <TerminalDetailOverlay
      isOpen={Boolean(activeCompanyDetail)}
      onClose={() => setActiveCompanyKey(null)}
      title={activeCompanyDetail?.title || "Company"}
      subtitle={activeCompanyDetail?.subtitle || "ls company/"}
      lines={activeCompanyDetail?.lines || []}
      storageKey={activeCompanyDetail?.storageKey}
      ctaLabel="saghallo"
      ctaMode="inlineContact"
      onPrev={handlePrev}
      onNext={handleNext}
      hasPrev={activeCompanyIndex > 0}
      hasNext={activeCompanyIndex >= 0 && activeCompanyIndex < orderedCompanyKeys.length - 1}
    />
  );

  const terminal = (
    <div className="rounded-lg border border-[#AC8E66]/20 overflow-hidden shadow-2xl shadow-black/40 font-mono">
      <div className="flex items-center gap-2 px-4 py-2.5 bg-[#1a1a1a] border-b border-[#AC8E66]/10">
        <span className="w-3 h-3 rounded-full bg-[#ff5f57]" />
        <span className="w-3 h-3 rounded-full bg-[#febc2e]" />
        <span className="w-3 h-3 rounded-full bg-[#28c840]" />
        <span className="ml-3 text-xs text-[#AC8E66] select-none">vertrauen</span>
      </div>
      <div className="px-4 sm:px-6 py-5 bg-[#0F0F0F]">
        <p className="text-sm text-[#EFEBDC]/50 mb-4">
          <span className="text-[#AC8E66]">$</span> trust --verified
        </p>
        <div className="flex flex-wrap justify-center gap-4 sm:gap-5">
          {companies.map((company) => (
            <div
              key={company.name}
              onClick={() => handleCompanyClick(company.name)}
              className="relative w-[60px] h-[60px] sm:w-[68px] sm:h-[68px] flex items-center justify-center bg-[#1c1c1c] rounded-full group cursor-pointer"
              title={company.name}
            >
              <RoughCircle size={60} color="#AC8E66" strokeWidth={1} roughness={0.8} className="sm:hidden" />
              <RoughCircle size={68} color="#AC8E66" strokeWidth={1} roughness={0.8} className="hidden sm:block" />
              <img
                src={company.image}
                alt={company.name}
                className={`object-contain opacity-60 group-hover:opacity-100 transition-opacity ${
                  company.dark ? "brightness-0 invert" : ""
                } ${company.large ? "w-10 h-10 sm:w-12 sm:h-12" : "w-6 h-6 sm:w-7 sm:h-7"}`}
              />
            </div>
          ))}
        </div>
        <p className="text-xs text-green-400/70 mt-5 pt-4 border-t border-[#AC8E66]/10">
          <span className="text-[#AC8E66]/80">&gt;</span> {companies.length} partner verifiziert.{" "}
          <span
            onClick={() => {
              if (enableDetailOverlay && companies[0]?.name) {
                handleCompanyClick(companies[0].name);
                return;
              }
              onCompanyClick?.();
            }}
            className="text-[#AC8E66]/70 hover:text-[#AC8E66] cursor-pointer underline underline-offset-2 transition-colors"
          >
            details anzeigen →
          </span>
        </p>
      </div>
    </div>
  );

  if (!withBackground) {
    return <>{terminal}{overlay}</>;
  }

  return (
    <PaperGradientBG variant="zen151515">
      <section className="py-12 md:py-20">
        <div className="max-w-3xl mx-auto px-6">{terminal}</div>
      </section>
      {overlay}
    </PaperGradientBG>
  );
}
